import { forwardRef, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Files } from '@/components/ui/file'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'

import type { DocumentPage } from '@/lib/api/client'

import {
  useCreateVideo,
  useListEnrichedVideos,
} from '@/services/queries/videos'
import { cn } from '@/utils'
import { Tabs, TabsTrigger, TabsList, TabsContent } from '@/components/ui/tabs'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { ImagesList } from './ImagesList'
import { VisuallyHidden } from '@radix-ui/themes'
import { useDocumentState } from '@/state'
import { ImagePlaceholder } from './DocumentPageToolbar/DraggablePlaceholders'

interface UploadVideoButtonProps {
  documentPage: DocumentPage
  children?: React.ReactNode
  isDisabled?: boolean
}

type FormInputs = {
  files: File[]
}

const formSchema = z.object({
  files: z.array(z.any()).min(1, {
    message: 'Upload at least 1 video.',
  }),
})

export const UploadVideoButton = forwardRef<
  any,
  UploadVideoButtonProps & {
    open?: boolean
    onOpenChange?: (open: boolean) => void
    renderTrigger?: boolean
  }
>(
  (
    {
      documentPage,
      children,
      isDisabled,
      open: controlledOpen,
      onOpenChange,
      renderTrigger = true,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const open = controlledOpen ?? isOpen
    const setOpen = onOpenChange ?? setIsOpen

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {renderTrigger && (
          <DialogTrigger
            disabled={isDisabled}
            className={cn(
              'flex flex-row items-center space-x-2 border-0 shadow-none w-full justify-start text-left h-full p-2',
              {
                'opacity-50': isDisabled,
                'hover:bg-accent': !isDisabled,
                'hover:text-accent-foreground': !isDisabled,
              },
            )}
            onClick={() => setOpen(true)}
          >
            {children}
          </DialogTrigger>
        )}
        <DialogContent
          className="min-h-44"
          ref={ref}
          ariaLabel="Upload video dialog"
        >
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle>Upload new Video</DialogTitle>
            </DialogHeader>
          </VisuallyHidden>
          <Tabs defaultValue="select" className="w-full">
            <TabsList className="my-5 border-b w-full justify-start">
              <TabsTrigger value="select">Use existing videos</TabsTrigger>
              <TabsTrigger value="upload">Upload new</TabsTrigger>
            </TabsList>
            <TabsContent value="select">
              <SelectVideosList
                documentPageId={documentPage?.id as string}
                documentVersionId={documentPage.document_version}
                closeDialog={() => setOpen(false)}
              />
            </TabsContent>
            <TabsContent value="upload">
              <UploadForm
                documentPage={documentPage}
                closeModal={() => setOpen(false)}
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    )
  },
)

UploadVideoButton.displayName = 'UploadVideoButton'

type UploadVideoFormProps = {
  closeModal: () => void
  documentPage: DocumentPage
}
const UploadForm = ({ closeModal, documentPage }: UploadVideoFormProps) => {
  const form = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  })

  const submitButtonPosRef = useRef<{
    clientX: number
    clientY: number
  } | null>(null)

  const handlePointerDown = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    submitButtonPosRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
    }
  }

  const { isPending, mutate: createVideo } = useCreateVideo({
    onSuccess: () => {
      closeModal()
    },
  })

  const onSubmit = ({ files }: FormInputs) => {
    createVideo({
      documentPage,
      file: files[0],
      position: submitButtonPosRef as any,
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Files
          placeholder="Upload a Video up to 100 MB."
          onChange={(files) => {
            form.setValue('files', files)
          }}
          allowMultiple={false}
          files={form.watch('files')}
          validateFile={(file) => {
            form.setError('files', {})
            const validExtensions = ['mp4', 'webm', 'ogg', 'm3u8', 'mpd']
            const tok = file.name.toLowerCase().split('.')
            const ext = tok[tok.length - 1]

            const hasValidFileExtension = validExtensions.includes(ext)
            if (!hasValidFileExtension) {
              form.setError('files', {
                type: 'extention',
                message: `Only files with the following extensions are allowed: ${validExtensions.join(
                  ', ',
                )}.`,
              })
            }

            const maxFileSize = 100428800 // 100 MB
            const hasValidFileSize = file.size <= maxFileSize

            if (!hasValidFileSize) {
              form.setError('files', {
                type: 'max',
                message: 'Video must be smaller than 100MB.',
              })
            }

            const isValid = hasValidFileExtension && hasValidFileExtension

            return isValid
          }}
          error={form.formState.errors.files?.message}
        />
        <Button
          type="submit"
          disabled={isPending}
          onPointerDown={handlePointerDown}
        >
          {isPending ? (
            <div className="flex items-center space-x-2">
              <span>Uploading</span>
              <Loading />
            </div>
          ) : (
            'Upload Video'
          )}
        </Button>
      </form>
    </Form>
  )
}

type SelectVideosListProps = {
  documentPageId: string
  documentVersionId: string
  closeDialog: () => void
}
const SelectVideosList = ({
  documentPageId,
  documentVersionId,
  closeDialog,
}: SelectVideosListProps) => {
  const { isLoading: isReadOnlyInfoLoading, data: readOnlyInfo } =
    useIsReadOnly()
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )
  const { data: videos, isLoading: isLoadingVideos } = useListEnrichedVideos({
    documentVersionId,
  })
  const isReadOnly = useMemo(
    () => Boolean(readOnlyInfo?.isReadOnly),
    [readOnlyInfo?.isReadOnly],
  )

  return (
    <div className="flex max-h-[650px] flex-col overflow-y-scroll">
      <div className="flex flex-col">
        <h3 className="text-lg font-bold">Video Gallery</h3>
        <ImagesList
          showName={true}
          images={videos.map((video) => ({
            ...video,
            download_url: video.thumbnail as string,
          }))}
          isLoading={isLoadingVideos || isReadOnlyInfoLoading}
          isReadOnly={isReadOnly}
          onClick={({ image, event }) => {
            startAddingElement({
              newElementData: {
                Component: () => (
                  <ImagePlaceholder src={image.download_url as string} />
                ),
                dataType: 'video',
                attributes: {
                  videoId: image.id,
                },
                documentPageId,
              },
              triggerLocation: {
                x: event.clientX,
                y: event.clientY,
              },
            })
            closeDialog()
          }}
        />
      </div>
    </div>
  )
}
